import React, { Fragment, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { Dialog, Transition, Menu } from '@headlessui/react';
import {
  Squares2X2Icon,
  ClipboardDocumentListIcon,
  QuestionMarkCircleIcon,
  SparklesIcon,
  ChartBarIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  BellIcon,
  BoltIcon,
  ChevronDownIcon,
  UserCircleIcon,
  PlusIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useAuth } from '../../context/AuthContext';

// ─── Navigation definition ────────────────────────────────────────────────────
const navigation = [
  {
    section: 'Main',
    items: [
      { label: 'Dashboard', to: '/dashboard', icon: Squares2X2Icon },
    ],
  },
  {
    section: 'Assessments',
    items: [
      { label: 'All Assessments', to: '/assessments', icon: ClipboardDocumentListIcon },
      { label: 'New Assessment',  to: '/assessments/new', icon: PlusIcon },
    ],
  },
  {
    section: 'Bootcamps',
    items: [
      { label: 'All Bootcamps', to: '/bootcamps',     icon: RocketLaunchIcon },
      { label: 'New Bootcamp',  to: '/bootcamps/new', icon: PlusIcon },
    ],
  },
  {
    section: 'Questions',
    items: [
      { label: 'Question Library', to: '/questions',          icon: QuestionMarkCircleIcon },
      { label: 'AI Generator',     to: '/questions/generate', icon: SparklesIcon },
    ],
  },
  {
    section: 'Analytics',
    items: [
      { label: 'Reports', to: '/reports', icon: ChartBarIcon },
    ],
  },
  {
    section: 'System',
    items: [
      { label: 'Settings', to: '/settings', icon: CogIcon },
    ],
  },
];

// ─── Shared nav link styles ────────────────────────────────────────────────────
function SidebarLink({ item, onClick }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.to === '/dashboard'}
      onClick={onClick}
      className={({ isActive }) =>
        clsx(
          'group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          isActive
            ? 'bg-primary-600 text-white'
            : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon
            className={clsx(
              'h-5 w-5 shrink-0 transition-colors',
              isActive ? 'text-white' : 'text-neutral-400 group-hover:text-white'
            )}
            aria-hidden="true"
          />
          {item.label}
        </>
      )}
    </NavLink>
  );
}

// ─── Sidebar content (shared between desktop & mobile) ───────────────────────
function SidebarContent({ onLinkClick }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  function handleLogout() {
    logout();
    navigate('/login');
  }

  const initials = user
    ? `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase() || user.email?.[0]?.toUpperCase() || 'U'
    : 'U';

  const displayName = user
    ? [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email
    : 'User';

  return (
    <div className="flex h-full flex-col">
      {/* Brand */}
      <div className="flex h-16 shrink-0 items-center gap-2.5 border-b border-neutral-800 px-5">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-600">
          <BoltIcon className="h-5 w-5 text-white" aria-hidden="true" />
        </div>
        <span className="text-base font-bold text-white font-heading tracking-tight">
          FounderMind <span className="text-primary-400">AI</span>
        </span>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 scrollbar-thin scrollbar-thumb-neutral-700">
        <ul className="space-y-5">
          {navigation.map((group) => (
            <li key={group.section}>
              <p className="mb-1.5 px-3 text-xs font-semibold uppercase tracking-widest text-neutral-500">
                {group.section}
              </p>
              <ul className="space-y-0.5">
                {group.items.map((item) => (
                  <li key={item.to}>
                    <SidebarLink item={item} onClick={onLinkClick} />
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </nav>

      {/* User footer */}
      <div className="shrink-0 border-t border-neutral-800 p-3">
        <div className="flex items-center gap-3 rounded-lg px-2 py-2">
          {/* Avatar */}
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-500 text-xs font-bold text-white">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-white">{displayName}</p>
            {user?.email && (
              <p className="truncate text-xs text-neutral-400">{user.email}</p>
            )}
          </div>
          {/* Logout */}
          <button
            type="button"
            onClick={handleLogout}
            title="Sign out"
            className="shrink-0 rounded-lg p-1.5 text-neutral-400 transition hover:bg-neutral-800 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <ArrowRightOnRectangleIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── DashboardLayout ──────────────────────────────────────────────────────────
export default function DashboardLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  function handleLogout() {
    logout();
    navigate('/login');
  }

  const initials = user
    ? `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase() || user.email?.[0]?.toUpperCase() || 'U'
    : 'U';

  const displayName = user
    ? [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email
    : 'User';

  return (
    <div className="flex h-screen overflow-hidden bg-surface">
      {/* ── Mobile sidebar drawer ── */}
      <Transition show={mobileOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50 lg:hidden"
          onClose={() => setMobileOpen(false)}
        >
          {/* Overlay */}
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-neutral-900/70 backdrop-blur-sm" aria-hidden="true" />
          </Transition.Child>

          {/* Drawer panel */}
          <Transition.Child
            as={Fragment}
            enter="transform transition ease-in-out duration-300"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transform transition ease-in-out duration-200"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <Dialog.Panel className="fixed inset-y-0 left-0 w-64 bg-neutral-900">
              {/* Close button inside drawer */}
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="absolute right-3 top-3.5 z-10 rounded-lg p-1.5 text-neutral-400 hover:bg-neutral-800 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                aria-label="Close sidebar"
              >
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
              <SidebarContent onLinkClick={() => setMobileOpen(false)} />
            </Dialog.Panel>
          </Transition.Child>
        </Dialog>
      </Transition>

      {/* ── Desktop sidebar (static) ── */}
      <aside className="hidden w-64 shrink-0 bg-neutral-900 lg:flex lg:flex-col">
        <SidebarContent />
      </aside>

      {/* ── Main area ── */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="flex h-16 shrink-0 items-center justify-between border-b border-neutral-200 bg-white px-4 shadow-sm sm:px-6">
          {/* Left: hamburger (mobile) */}
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="rounded-button p-2 text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 lg:hidden"
            aria-label="Open sidebar"
          >
            <Bars3Icon className="h-5 w-5" aria-hidden="true" />
          </button>

          {/* Desktop: brand name (small) or breadcrumb placeholder */}
          <span className="hidden text-sm font-semibold text-neutral-400 lg:block font-heading">
            FounderMind AI
          </span>

          {/* Right controls */}
          <div className="flex items-center gap-2">
            {/* Notification bell */}
            <button
              type="button"
              className="relative rounded-button p-2 text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              aria-label="Notifications"
            >
              <BellIcon className="h-5 w-5" aria-hidden="true" />
              {/* Unread dot */}
              <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-error-500 ring-2 ring-white" />
            </button>

            {/* User dropdown */}
            <Menu as="div" className="relative">
              <Menu.Button className="flex items-center gap-2 rounded-button py-1.5 pl-2 pr-3 text-sm font-medium text-neutral-700 transition hover:bg-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-500 text-xs font-bold text-white">
                  {initials}
                </div>
                <span className="hidden sm:block">{displayName}</span>
                <ChevronDownIcon className="h-4 w-4 text-neutral-400" aria-hidden="true" />
              </Menu.Button>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 mt-1.5 w-48 origin-top-right rounded-card bg-white py-1 shadow-card-hover ring-1 ring-neutral-200 focus:outline-none">
                  <div className="border-b border-neutral-100 px-4 py-2.5">
                    <p className="truncate text-sm font-medium text-neutral-900">{displayName}</p>
                    {user?.email && (
                      <p className="truncate text-xs text-neutral-500">{user.email}</p>
                    )}
                  </div>

                  <Menu.Item>
                    {({ active }) => (
                      <NavLink
                        to="/settings/profile"
                        className={clsx(
                          'flex items-center gap-2.5 px-4 py-2 text-sm text-neutral-700',
                          active && 'bg-neutral-50'
                        )}
                      >
                        <UserCircleIcon className="h-4 w-4 text-neutral-400" aria-hidden="true" />
                        Profile
                      </NavLink>
                    )}
                  </Menu.Item>

                  <Menu.Item>
                    {({ active }) => (
                      <NavLink
                        to="/settings"
                        className={clsx(
                          'flex items-center gap-2.5 px-4 py-2 text-sm text-neutral-700',
                          active && 'bg-neutral-50'
                        )}
                      >
                        <CogIcon className="h-4 w-4 text-neutral-400" aria-hidden="true" />
                        Settings
                      </NavLink>
                    )}
                  </Menu.Item>

                  <div className="my-1 border-t border-neutral-100" />

                  <Menu.Item>
                    {({ active }) => (
                      <button
                        type="button"
                        onClick={handleLogout}
                        className={clsx(
                          'flex w-full items-center gap-2.5 px-4 py-2 text-sm text-error-600',
                          active && 'bg-error-50'
                        )}
                      >
                        <ArrowRightOnRectangleIcon className="h-4 w-4" aria-hidden="true" />
                        Sign out
                      </button>
                    )}
                  </Menu.Item>
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-surface p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
